import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Send, Loader2, Volume2, VolumeX, Camera, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { auth } from '../lib/firebase';

// Cache buster per evitare problemi di cache immagini
const CACHE_VERSION = 'v3';

// Pose di Emme - 7 espressioni diverse (file locali per evitare problemi CDN)
const EMME_POSES = {
  normal: `/emme/normal.png?${CACHE_VERSION}`,
  love: `/emme/love.png?${CACHE_VERSION}`,
  idea: `/emme/idea.png?${CACHE_VERSION}`,
  thinking: `/emme/thinking.png?${CACHE_VERSION}`,
  sad: `/emme/sad.png?${CACHE_VERSION}`,
  happy: `/emme/happy.png?${CACHE_VERSION}`,
  talking: `/emme/talking.png?${CACHE_VERSION}`
};

// Messaggi di benvenuto randomici
const WELCOME_MESSAGES = [
  "Ciao! Sono Emme, la tua guida marina! Come posso aiutarti oggi?",
  "Bentornato in Respiro Blu! Hai domande sul mare?",
  "Ciao amico! Pronto per una nuova avventura marina?",
  "Ehi! Felice di rivederti! Cosa vuoi scoprire oggi?"
];

const EmmeAssistant = ({ isOpen, onClose }) => {
  const [currentPose, setCurrentPose] = useState('normal');
  const [message, setMessage] = useState('');
  const [response, setResponse] = useState('');
  const [loading, setLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showWelcome, setShowWelcome] = useState(true);
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [imageError, setImageError] = useState(false);
  const [audioData, setAudioData] = useState(null);
  const [chatHistory, setChatHistory] = useState([]); // Cronologia chat
  const [selectedImage, setSelectedImage] = useState(null); // Immagine da inviare
  const [imagePreview, setImagePreview] = useState(null); // Preview immagine
  const audioRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  // Animazione di galleggiamento continua
  const [floatOffset, setFloatOffset] = useState(0);

  useEffect(() => {
    const floatInterval = setInterval(() => {
      setFloatOffset(prev => (prev + 1) % 360);
    }, 50);
    return () => clearInterval(floatInterval);
  }, []);

  // Messaggio di benvenuto all'apertura
  useEffect(() => {
    if (isOpen && showWelcome) {
      const randomWelcome = WELCOME_MESSAGES[Math.floor(Math.random() * WELCOME_MESSAGES.length)];
      setWelcomeMessage(randomWelcome);
      setCurrentPose('happy');
      
      const timer = setTimeout(() => {
        setCurrentPose('normal');
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [isOpen, showWelcome]);

  // Focus sull'input quando si apre
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  // Funzione per determinare l'emozione dalla risposta
  const detectEmotion = useCallback((text) => {
    const lowerText = text.toLowerCase();
    
    if (lowerText.includes('plastica') || lowerText.includes('inquinamento') || lowerText.includes('rifiuti') || lowerText.includes('purtroppo')) {
      return 'sad';
    }
    if (lowerText.includes('bellissim') || lowerText.includes('meravigli') || lowerText.includes('fantastico') || lowerText.includes('amo') || lowerText.includes('adoro')) {
      return 'love';
    }
    if (lowerText.includes('idea') || lowerText.includes('suggerimento') || lowerText.includes('consiglio') || lowerText.includes('ecco')) {
      return 'idea';
    }
    if (lowerText.includes('!') && (lowerText.includes('brav') || lowerText.includes('ottimo') || lowerText.includes('perfetto'))) {
      return 'happy';
    }
    
    return 'happy';
  }, []);

  // Gestione upload immagine
  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Verifica tipo file
    if (!file.type.startsWith('image/')) {
      toast.error('Seleziona un\'immagine valida');
      return;
    }
    
    // Verifica dimensione (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('L\'immagine è troppo grande (max 5MB)');
      return;
    }
    
    // Converti in base64
    const reader = new FileReader();
    reader.onload = (e) => {
      setSelectedImage(e.target.result);
      setImagePreview(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  // Rimuovi immagine selezionata
  const removeImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Invia messaggio
  const handleSend = async () => {
    if ((!message.trim() && !selectedImage) || loading) return;
    
    const userMessage = message.trim();
    const hasImage = !!selectedImage;
    setMessage('');
    setLoading(true);
    setResponse('');
    setAudioData(null);
    setShowWelcome(false);
    setCurrentPose('thinking');

    try {
      // Ottieni token fresco da Firebase Auth
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('Devi essere autenticato per parlare con Emme');
      }
      const token = await currentUser.getIdToken(true);
      
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      // Aggiungi messaggio utente alla cronologia
      const newUserMessage = {
        role: 'user',
        content: userMessage || (hasImage ? 'Cosa vedi in questa immagine?' : ''),
        hasImage: hasImage
      };
      
      // Prepara la richiesta con cronologia e eventuale immagine
      const requestBody = {
        question: userMessage || (hasImage ? 'Analizza questa immagine e dimmi cosa vedi. Rispondi in italiano.' : ''),
        chat_history: chatHistory.slice(-10), // Ultimi 10 messaggi per contesto
        image: hasImage ? selectedImage : null
      };
      
      // Usa endpoint con immagine se presente, altrimenti normale
      const endpoint = hasImage ? '/api/turtle/ask-with-image' : '/api/turtle/ask';
      
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Errore nella risposta');
      }

      const data = await res.json();
      
      // Mostra "idea" se la risposta contiene suggerimenti
      if (data.text.toLowerCase().includes('ecco') || data.text.toLowerCase().includes('suggerisco') || data.text.toLowerCase().includes('consiglio')) {
        setCurrentPose('idea');
      } else {
        setCurrentPose('talking');
      }
      
      setResponse(data.text);
      
      // Aggiorna cronologia chat
      setChatHistory(prev => [
        ...prev,
        newUserMessage,
        { role: 'assistant', content: data.text }
      ]);
      
      // Salva audio se presente
      if (data.audio) {
        setAudioData(data.audio);
      }
      
      // Rimuovi immagine dopo invio
      removeImage();
      
      setTimeout(() => {
        const emotion = detectEmotion(data.text);
        setCurrentPose(emotion);
      }, 1500);

      setTimeout(() => {
        setCurrentPose('normal');
      }, 5000);

    } catch (error) {
      console.error('Emme error:', error);
      setCurrentPose('sad');
      
      // Mostra messaggio di errore più specifico
      let errorMessage = 'Mi dispiace, non riesco a rispondere in questo momento.';
      if (error.message) {
        if (error.message.includes('autenticato')) {
          errorMessage = 'Devi effettuare il login per parlare con me!';
        } else if (error.message.includes('API key') || error.message.includes('chiave') || error.message.includes('configurazione')) {
          errorMessage = 'C\'è un problema con la configurazione. Contatta l\'amministratore.';
        } else if (error.message.includes('quota') || error.message.includes('limit') || error.message.includes('limite')) {
          errorMessage = 'Ho raggiunto il limite di risposte per oggi. Riprova domani!';
        } else if (error.message.includes('Token') || error.message.includes('401') || error.message.includes('scadut')) {
          errorMessage = 'Sessione scaduta. Ricarica la pagina e riprova.';
        }
      }
      setResponse(errorMessage);
      
      setTimeout(() => setCurrentPose('normal'), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Riproduci audio
  const playAudio = () => {
    if (!audioData) return;
    
    try {
      // Ferma audio precedente se in riproduzione
      if (audioRef.current) {
        audioRef.current.pause();
      }
      
      const audio = new Audio(`data:audio/mp3;base64,${audioData}`);
      audioRef.current = audio;
      
      audio.onplay = () => {
        setIsPlaying(true);
        setCurrentPose('talking');
      };
      
      audio.onended = () => {
        setIsPlaying(false);
        setCurrentPose('normal');
      };
      
      audio.onerror = () => {
        setIsPlaying(false);
        toast.error('Errore nella riproduzione audio');
      };
      
      audio.play().catch(err => {
        console.error('Audio play error:', err);
        toast.error('Non riesco a riprodurre l\'audio');
      });
    } catch (error) {
      console.error('Audio error:', error);
    }
  };

  // Stop audio
  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  };

  // Calcola offset galleggiamento
  const floatY = Math.sin(floatOffset * Math.PI / 180) * 8;
  const floatRotate = Math.sin(floatOffset * Math.PI / 180) * 2;

  // Fallback emoji se immagine non carica
  const handleImageError = () => {
    setImageError(true);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div 
        className="w-full max-w-md bg-gradient-to-b from-slate-800 to-slate-900 rounded-3xl shadow-2xl border border-cyan-500/30 overflow-hidden animate-slide-up"
        style={{ maxHeight: '85vh' }}
      >
        {/* Header */}
        <div className="relative bg-gradient-to-r from-cyan-600/20 to-teal-600/20 p-4">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-2 rounded-full bg-slate-700/50 hover:bg-slate-600/50 transition-colors"
            data-testid="emme-close-button"
          >
            <X className="w-5 h-5 text-white" />
          </button>
          
          <div className="flex items-center gap-3">
            <div className="text-center">
              <h2 className="text-white font-bold text-lg">Emme</h2>
              <p className="text-cyan-400 text-xs">La tua guida marina</p>
            </div>
          </div>
        </div>

        {/* Emme Character Display */}
        <div className="relative h-56 bg-gradient-to-b from-cyan-900/30 to-transparent flex items-center justify-center overflow-hidden">
          {/* Bolle decorative */}
          <div className="absolute inset-0 pointer-events-none">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="absolute w-3 h-3 bg-white/10 rounded-full animate-bubble"
                style={{
                  left: `${15 + i * 15}%`,
                  animationDelay: `${i * 0.5}s`,
                  animationDuration: `${3 + i * 0.5}s`
                }}
              />
            ))}
          </div>
          
          {/* Emme animata */}
          <div 
            className="relative transition-all duration-500 ease-out"
            style={{
              transform: `translateY(${floatY}px) rotate(${floatRotate}deg)`,
            }}
          >
            {imageError ? (
              <div className="w-40 h-40 flex items-center justify-center text-8xl">
                🐢
              </div>
            ) : (
              <img
                src={EMME_POSES[currentPose]}
                alt={`Emme - ${currentPose}`}
                className="w-40 h-40 object-contain drop-shadow-2xl transition-opacity duration-300"
                style={{
                  filter: 'drop-shadow(0 10px 20px rgba(0, 200, 255, 0.3))'
                }}
                onError={handleImageError}
              />
            )}
            
            {/* Indicatore stato */}
            {loading && (
              <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1 bg-slate-800/80 px-3 py-1 rounded-full">
                <Loader2 className="w-3 h-3 text-cyan-400 animate-spin" />
                <span className="text-cyan-400 text-xs">Sto pensando...</span>
              </div>
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div className="p-4 space-y-3 max-h-32 overflow-y-auto">
          {(showWelcome && welcomeMessage) && (
            <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-2xl p-3">
              <p className="text-cyan-100 text-sm">{welcomeMessage}</p>
            </div>
          )}
          
          {response && (
            <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-2xl p-3">
              <p className="text-cyan-100 text-sm whitespace-pre-wrap">{response}</p>
              
              {/* Pulsante Audio */}
              {audioData && (
                <button
                  onClick={isPlaying ? stopAudio : playAudio}
                  className="mt-3 flex items-center gap-2 px-4 py-2 bg-cyan-500/20 hover:bg-cyan-500/30 rounded-xl transition-colors w-full justify-center"
                  data-testid="emme-audio-button"
                >
                  {isPlaying ? (
                    <>
                      <VolumeX className="w-4 h-4 text-cyan-400" />
                      <span className="text-cyan-300 text-sm font-medium">Ferma audio</span>
                    </>
                  ) : (
                    <>
                      <Volume2 className="w-4 h-4 text-cyan-400" />
                      <span className="text-cyan-300 text-sm font-medium">Ascolta Emme</span>
                    </>
                  )}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Input Area - FIXED per Android */}
        <div className="p-4 border-t border-slate-700/50 pb-safe">
          {/* Preview immagine selezionata */}
          {imagePreview && (
            <div className="mb-3 relative inline-block">
              <img 
                src={imagePreview} 
                alt="Preview" 
                className="w-20 h-20 object-cover rounded-xl border-2 border-cyan-500/50"
              />
              <button
                onClick={removeImage}
                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white text-xs"
              >
                ✕
              </button>
            </div>
          )}
          
          <div className="flex items-center gap-2">
            {/* Input file nascosto */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageSelect}
              className="hidden"
              data-testid="emme-file-input"
            />
            
            {/* Pulsante foto */}
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={loading}
              className="flex-shrink-0 p-3 bg-slate-700/50 hover:bg-slate-600/50 rounded-xl text-cyan-400 disabled:opacity-50 transition-all min-w-[48px] min-h-[48px] flex items-center justify-center"
              title="Invia una foto"
              data-testid="emme-photo-button"
            >
              <Camera className="w-5 h-5" />
            </button>
            
            <input
              ref={inputRef}
              type="text"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSend()}
              placeholder={selectedImage ? "Chiedi info sulla foto..." : "Chiedi qualcosa a Emme..."}
              className="flex-1 min-w-0 bg-slate-700/50 border border-slate-600/50 rounded-xl px-4 py-3 text-white placeholder-slate-400 focus:outline-none focus:border-cyan-500/50 text-base"
              disabled={loading}
              data-testid="emme-input"
            />
            
            <button
              onClick={handleSend}
              disabled={loading || (!message.trim() && !selectedImage)}
              className="flex-shrink-0 p-3 bg-gradient-to-r from-cyan-500 to-teal-500 rounded-xl text-white disabled:opacity-50 disabled:cursor-not-allowed hover:from-cyan-400 hover:to-teal-400 transition-all min-w-[48px] min-h-[48px] flex items-center justify-center"
              data-testid="emme-send-button"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </div>
          
          {/* Suggerimenti rapidi */}
          <div className="flex flex-wrap gap-2 mt-3">
            {['Proteggere il mare', 'Parlami di te', 'Cos\'è questo?'].map((suggestion) => (
              <button
                key={suggestion}
                onClick={() => setMessage(suggestion)}
                className="text-xs bg-slate-700/30 hover:bg-slate-600/30 text-slate-400 hover:text-white px-3 py-1.5 rounded-full transition-colors"
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      </div>
      
      {/* CSS Animations */}
      <style>{`
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(100px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes bubble {
          0% {
            transform: translateY(100%) scale(0);
            opacity: 0;
          }
          50% {
            opacity: 0.5;
          }
          100% {
            transform: translateY(-200px) scale(1);
            opacity: 0;
          }
        }
        
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
        
        .animate-bubble {
          animation: bubble 4s ease-in-out infinite;
        }
        
        .pb-safe {
          padding-bottom: max(1rem, env(safe-area-inset-bottom));
        }
      `}</style>
    </div>
  );
};

// Popup di benvenuto quando si apre l'app
export const EmmeWelcomePopup = ({ isOpen, onClose, onOpenChat }) => {
  const [floatOffset, setFloatOffset] = useState(0);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setFloatOffset(prev => (prev + 1) % 360);
    }, 50);
    return () => clearInterval(interval);
  }, []);
  
  const floatY = Math.sin(floatOffset * Math.PI / 180) * 6;
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-gradient-to-b from-slate-800 to-slate-900 rounded-3xl p-6 max-w-sm w-full border border-cyan-500/30 shadow-2xl animate-bounce-in">
        {/* Emme */}
        <div className="flex justify-center mb-4">
          <div style={{ transform: `translateY(${floatY}px)` }} className="transition-transform">
            <img
              src={EMME_POSES.happy}
              alt="Emme ti saluta"
              className="w-32 h-32 object-contain drop-shadow-lg"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
        </div>
        
        {/* Messaggio */}
        <div className="text-center mb-6">
          <h2 className="text-xl font-bold text-white mb-2">Ciao! Sono Emme! 🐢</h2>
          <p className="text-cyan-300 text-sm">
            La tua guida marina. Sono qui per aiutarti a scoprire i segreti del mare e come proteggerlo!
          </p>
        </div>
        
        {/* Pulsanti */}
        <div className="flex flex-col gap-3">
          <button
            onClick={() => {
              onClose();
              onOpenChat();
            }}
            className="w-full py-3 bg-gradient-to-r from-cyan-500 to-teal-500 text-white font-semibold rounded-xl hover:from-cyan-400 hover:to-teal-400 transition-all"
            data-testid="emme-welcome-chat-button"
          >
            Chatta con me!
          </button>
          <button
            onClick={onClose}
            className="w-full py-3 bg-slate-700/50 text-slate-300 font-medium rounded-xl hover:bg-slate-600/50 transition-all"
            data-testid="emme-welcome-close-button"
          >
            Più tardi
          </button>
        </div>
      </div>
      
      <style>{`
        @keyframes bounce-in {
          0% {
            opacity: 0;
            transform: scale(0.8) translateY(20px);
          }
          50% {
            transform: scale(1.02) translateY(-5px);
          }
          100% {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        .animate-bounce-in {
          animation: bounce-in 0.4s ease-out;
        }
      `}</style>
    </div>
  );
};

// Pulsante flottante per aprire Emme - con cache busting
export const EmmeButton = ({ onClick, isHiding = false }) => {
  const [floatOffset, setFloatOffset] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setFloatOffset(prev => (prev + 1) % 360);
    }, 50);
    return () => clearInterval(interval);
  }, []);
  
  const floatY = Math.sin(floatOffset * Math.PI / 180) * 4;
  
  return (
    <button
      onClick={onClick}
      className={`fixed bottom-24 right-4 w-16 h-16 rounded-full shadow-lg shadow-cyan-500/30 flex items-center justify-center transition-all duration-500 ease-in-out z-40 overflow-hidden border-2 border-cyan-400 bg-white ${
        isHiding ? 'translate-y-32 opacity-0 pointer-events-none' : 'opacity-100 hover:scale-110'
      }`}
      style={{
        transform: isHiding ? 'translateY(128px)' : `translateY(${floatY}px)`,
      }}
      title="Chiedi a Emme"
      data-testid="emme-assistant-button"
    >
      {imageError ? (
        <span className="text-3xl">🐢</span>
      ) : (
        <img 
          src={EMME_POSES.normal}
          alt="Emme" 
          className={`w-14 h-14 object-contain transition-opacity ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageError(true)}
        />
      )}
      {!imageLoaded && !imageError && (
        <span className="text-3xl absolute">🐢</span>
      )}
    </button>
  );
};

export default EmmeAssistant;
